import { keepPreviousData, useQuery } from "@tanstack/react-query";
import * as timeseriesAdminApi from "@/lib/api/timeseries-admin";

/** The admin timeseries view's query keys — the one place they are spelled out. */
export const timeseriesAdminKeys = {
  /** Every list page and filter; also the prefix for invalidating them all. */
  list: ["timeseriesInstruments"] as const,
  listPage: (params: object) => ["timeseriesInstruments", params] as const,
  data: (source: string | null, ticker: string | null) =>
    ["timeseriesInstrumentData", source, ticker] as const,
  statistics: (source: string | null, ticker: string | null) =>
    ["timeseriesInstrumentStatistics", source, ticker] as const,
};

/** Raw stored points for the inspect dialog; fetches only while a row is selected. */
export function useTimeseriesInstrumentData(source: string | null, ticker: string | null) {
  return useQuery({
    queryKey: timeseriesAdminKeys.data(source, ticker),
    queryFn: () => timeseriesAdminApi.getInstrumentData(source as string, ticker as string),
    enabled: !!source && !!ticker,
    staleTime: 60_000,
  });
}

/**
 * Calendar statistics for the inspect dialog's Statistics tab (QL-706); fetches
 * only while that tab is open for a selected row.
 */
export function useTimeseriesInstrumentStatistics(
  source: string | null,
  ticker: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: timeseriesAdminKeys.statistics(source, ticker),
    queryFn: () => timeseriesAdminApi.getInstrumentStatistics(source as string, ticker as string),
    enabled: enabled && !!source && !!ticker,
    staleTime: 60_000,
  });
}

export function useTimeseriesInstruments(params: {
  source?: string;
  search?: string;
  status?: string;
  page?: number;
  perPage?: number;
}) {
  return useQuery({
    queryKey: timeseriesAdminKeys.listPage(params),
    queryFn: () => timeseriesAdminApi.listInstruments(params),
    placeholderData: keepPreviousData,
  });
}
